import React, { useState } from 'react';
import { CapaFile, ViewerMode } from './types';
import useStyles from './useStyles';

import { AnnotationPinActive, AnnotationPinInactive, NoImage } from './assets';

import AppConfig from '@config';

import { Image } from '@components/Image';
import { clsx, colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { TabValue } from './useViewerTab';
import { CapaQuotationChatRoomSharedFiles } from '@generated/graphql';

interface Props {
  tab: TabValue;
  mode: ViewerMode;
  files: CapaQuotationChatRoomSharedFiles[];
  currentItem?: CapaQuotationChatRoomSharedFiles;
  onChangeIndex: (index: number) => void;
}

const ViewerFileList = ({
  tab,
  mode,
  files,
  currentItem,
  onChangeIndex
}: Props) => {
  const classes = useStyles();
  const isEmpty = files.length === 0;

  return (
    <div
      className={clsx(classes['Viewer-File-List-Items-root'], {
        default: mode === 'default',
        empty: isEmpty
      })}
    >
      {isEmpty && (
        <CustomizedTypography
          variant="body2"
          fontWeight="500"
          color="textSecondary"
        >
          {tab === 'CHAT' ? '채팅' : '요청서'}에서 첨부한 파일이 없습니다.
        </CustomizedTypography>
      )}
      {files.map((file, index) => {
        return (
          <FileItem
            key={index}
            index={index}
            file={file}
            isActive={file.fileId === currentItem?.fileId}
            onChangeIndex={onChangeIndex}
          />
        );
      })}
    </div>
  );
};

interface FileItemProps {
  index: number;
  isActive: boolean;
  file: CapaQuotationChatRoomSharedFiles;
  onChangeIndex: (index: number) => void;
}

const FileItem = ({ file, index, isActive, onChangeIndex }: FileItemProps) => {
  const isChat = window.location.pathname.includes('/chat');
  const classes = useStyles();
  const [noImage, setNoImage] = useState(false);

  const imageRegex = /(.*?)\.(jpg|png|jpeg)$/;

  const isImage =
    file.fileName && imageRegex.exec(file.fileName.toLocaleLowerCase())
      ? true
      : false;

  const src = isImage
    ? `${AppConfig.fileApiUrl}/file/${file?.fileId}`
    : `${AppConfig.hoopsViewerAPI}/${file?.fileId}.png`;

  const hasAnnotation = file.annotationStatus !== 'NONE';
  const unRead = file.annotationStatus === 'ON';

  return (
    <div
      className={clsx(classes['Viewer-File-List-Item-root'], {
        actived: isActive
      })}
      onClick={(e) => onChangeIndex(index)}
    >
      {hasAnnotation && isChat && (
        <div className="annotation-notice-pin">
          {unRead && <img src={AnnotationPinActive} alt="new" />}
          {!unRead && <img src={AnnotationPinInactive} alt="nothing new" />}
        </div>
      )}
      <div className="thumbnail">
        <Image
          width={noImage ? 24 : '100%'}
          height={noImage ? 24 : '100%'}
          style={{
            margin: noImage ? 2 : 0,
            backgroundColor: noImage ? colorSet.gray000 : undefined
          }}
          src={src}
          onError={(e) => {
            setNoImage(true);
            e.currentTarget.src = NoImage;
          }}
        />
      </div>
      {file.fileName && (
        <CustomizedTypography variant="body2" className="fileName">
          {file.fileName.length > 20
            ? file.fileName.slice(0, 17) +
              '...' +
              `${file.fileName
                .slice(file.fileName.lastIndexOf('.') + 1)
                .toLowerCase()}`
            : file.fileName}
        </CustomizedTypography>
      )}
    </div>
  );
};

export const FileAvatar = ({
  fileId,
  fileName,
  isActive
}: CapaFile & { isActive: boolean }) => {
  const classes = useStyles();

  const imageRegex = /(.*?)\.(jpg|png|jpeg)$/;

  const isImage =
    fileName && imageRegex.exec(fileName.toLocaleLowerCase()) ? true : false;

  const src = isImage
    ? `${AppConfig.fileApiUrl}/file/${fileId}`
    : `${AppConfig.hoopsViewerAPI}/${fileId}.png`;

  return (
    <div className={classes['Viewer-File-Avatar']}>
      <div className="avatar">
        <Image
          src={src}
          onError={(e) => {
            e.currentTarget.src = NoImage;
          }}
        />
      </div>
      <CustomizedTypography
        variant="body2"
        color="textPrimary"
        className="fileName"
      >
        {fileName}
      </CustomizedTypography>
    </div>
  );
};

export default ViewerFileList;
